<template>
  <div>
    <b-table :fields="fields"
             :items="attachmentsData"
             head-variant="light"
             hover
             small
             stacked="md">
      <template v-slot:cell(filename)="data">
        <div v-if="isDocumentInDb">
          <a :href="axios.defaults.baseURL + '/documents/download/' + data.item.id"
             target="_blank">
            {{ data.item.filename }}
          </a>
        </div>
        <div v-else>
          <span>
            {{ data.item.filename }}
          </span>
        </div>
      </template>

      <template v-slot:cell(action)="data">
        <base-icon-button-delete v-if="!disabled"
                                 title="Remove attachment"
                                 @click="removeFile(data.item)"/>
      </template>
    </b-table>

    <b-form-file ref="fileInput"
                 :accept="extensions"
                 :disabled="disabled || attachmentDisabled"
                 :placeholder="placeholders.attachment_placeholder"
                 variant="info"
                 @change="saveFile"/>
    <p v-if="formatMismatch" class="p-form-label d-block custom-text-danger mt-2">
      {{ placeholders.wrong_attachment_type }}
    </p>
  </div>
</template>

<script>
  import placeholders from '@/json/placeholders';
  import BaseIconButtonDelete from '@/components/base-components/base-icons/BaseIconButtonDelete';
  import { underscoresToHyphens } from '@/helpers';

  export default {
    name: 'AttachmentInput',
    components: { BaseIconButtonDelete },
    props: {
      value: {
        required: true,
        type: Array,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      attachmentUrl: {
        required: true,
        type: String,
      },
      attachmentKey: {
        type: String,
        default: '',
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      extensions: {
        type: String,
        default: 'application/pdf',
      },
    },
    data() {
      return {
        fields: ['filename', 'length', 'action'],
        attachments: this.value,
        attachmentsData: undefined,
        formatMismatch: false,
        isDocumentInDb: !!this.attachmentUrl,
        placeholders,
      };
    },
    computed: {
      attachmentDisabled() {
        if (!this.multiple && this.attachmentsData) {
          return this.attachmentsData.length > 0;
        }
        return false;
      },
      preparedAttachmentUrl() {
        return underscoresToHyphens(this.attachmentUrl);
      },
      params() {
        // eslint-disable-next-line camelcase
        return this.attachmentKey ? { attachment_key: this.attachmentKey } : {};
      },
    },
    watch: {
      value() {
        this.attachments = this.value;
        this.loadData();
      },
      attachments() {
        this.$emit('input', this.attachments);
      },
    },
    created() {
      this.loadData();
    },
    methods: {
      isFileSizeInBytesBelowThresholdInMB(fileSizeInBytes, mbThreshold) {
        const bytesInMegabyte = 1048576;
        const megabytes = fileSizeInBytes / bytesInMegabyte;
        return megabytes <= mbThreshold;
      },
      saveFile(e) {
        if (this.extensions.includes(e.target.files[0].type)) {
          const file = (e.target.files || e.dataTransfer.files)[0];
          if (this.isFileSizeInBytesBelowThresholdInMB(file.size, 10)) {
            this.formatMismatch = false;
            if (this.isDocumentInDb) {
              this.uploadFile(file);
            } else {
              this.attachments.push(file);
            }
          } else {
            this.$refs.fileInput.reset();
            this.formatMismatch = true;
            this.$notify({ type: 'error', title: 'Could not save because the file is too big!' });
          }
        } else {
          this.$refs.fileInput.reset();
          this.formatMismatch = true;
        }
      },
      uploadFile(file) {
        this.axios.put(
          `${this.preparedAttachmentUrl}/attachments/${file.name}`,
          file,
          {
            headers: { 'Content-Type': file.type },
            params: this.params,
          },
        )
          .then((response) => {
            const { msg, error, data } = response.data;
            if (msg) {
              this.$notify({ type: 'success', title: msg });
              this.attachments = data;
            } else {
              this.$notify({ type: 'error', title: error });
            }
          })
          .catch(() => {
            this.$notify({ type: 'error', title: 'Something went wrong' });
          });
      },
      loadDataFromCache() {
        this.attachmentsData = [];
        this.attachments.forEach(({ name: filename, size: length }) => {
          this.attachmentsData.push({ filename, length });
        });
      },
      removeFile(file) {
        if (this.isDocumentInDb) {
          this.removeFileFromDb(file);
        } else {
          this.removeFileFromCache(file);
        }
      },
      removeFileFromDb(file) {
        this.axios.delete(
          `${this.preparedAttachmentUrl}/attachments/${file.id}`,
          { params: this.params },
        )
          .then(
            (response) => {
              this.$refs.fileInput.reset();
              this.attachments = response.data.data;
            },
          );
        this.$emit('file-removed');
      },
      removeFileFromCache(file) {
        this.$refs.fileInput.reset();
        this.attachments = this.attachments.filter((el) => el.name !== file.filename);
        this.loadDataFromCache();
        this.$emit('file-removed');
      },
      loadData() {
        if (this.isDocumentInDb) {
          this.loadDataFromDb();
        } else {
          this.loadDataFromCache();
        }
      },
      loadDataFromDb() {
        this.axios.get(
          `${this.preparedAttachmentUrl}/attachments`,
          { params: this.params },
        )
          .then((response) => {
            this.attachmentsData = response.data;
          })
          .catch(() => {
            this.$notify({ type: 'error', title: 'Something went wrong' });
          });
      },
    },
  };
</script>

<style lang="scss"
       scoped>

</style>
